import React, { Component } from "react";
import PropTypes from "prop-types";
import Yup from "yup";
import { Form, Formik } from "formik";
import get from "lodash/get";
import has from "lodash/has";
import { withStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import ImageUploader from "core/ImageUploader";
import axios from "axios";
import {
  MenuItem,
  Select,
  TextField,
  Typography,
  FormHelperText,
  Chip,
  Checkbox,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CompositeDateTimePickers from "common/components/CompositeDateTimePickers";
import EnhancedInput from "common/components/EnhancedInput";
import EnhancedCard from "common/components/EnhancedCard";
import SimpleSnackbar from "common/components/SimpleSnackbar";
import find from "lodash/find";
import SubmitButton from "common/components/SubmitButton";
import ReactTextEditor from "common/components/ReactTextEditor";
import resourceFactory from "common/admin/resourceFactory";
import { Translation } from "react-i18next";
import { ValidImageFileTypes, MAX_FILE_SIZE } from "common/constants";
import UpdatePreview from "./Preview";
import { formatDate } from "../../common/utils/datetime";

class NotificationForm extends Component {
  state = {
    showBuildingsField: false,
    showFloorsField: false,
    showCompanyField: false,
    html_content: this.props.initialValues.html_content,
  };

  generateImageUrl = (file, setFieldValue) => {
    const action = resourceFactory("gcp_services/generateSignedUrl");
    action.create({ fileName: file.name }).then((data) => {
      axios({
        url: data.url,
        method: "PUT",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        data: file,
      }).then(() => {
        setFieldValue("image", data.publicUrl);
        this.setState({ image: data.publicUrl });
      });
    });
  };
  displayFloorLabelById(id, floors) {
    const floor = find(floors, ["id", id]);
    return `${floor?.level}`;
  }
  displayCompanyNameById(id, companies) {
    const copmany = find(companies, ["id", Number(id)]);
    return `${copmany?.name}`;
  }
  displayBuildingNameById(id, buildings) {
    const building = find(buildings, ["building_id", Number(id)]);
    return `${building?.building_name}`;
  }
  componentDidMount() {
    if (this.props.initialValues.target === "floor") {
      this.setState({ showFloorsField: true });
    }
    if (this.props.initialValues.target === "company") {
      this.setState({ showCompanyField: true });
    }
    if (this.props.initialValues.target === "building") {
      this.setState({ showBuildingsField: true });
    }
  }

  generateValidationSchema(t) {
    const { initialValues, isPublished } = this.props;

    return Yup.object().shape({
      title: Yup.string()
        .min(5, t("general_form_errors.min", { label: "Title", minValue: "5" }))
        .max(
          50,
          t("general_form_errors.max", { label: "Title", maxValue: "50" }),
        )
        .required(t("updates_create.about_title_error")),
      rsvp_form_url: Yup.string().nullable().url(t("general_form_errors.url")),
      max_registrations: Yup.number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value,
        )
        .min(
          1,
          t("general_form_errors.min", {
            label: "Max registrations",
            minValue: "1",
          }),
        )
        .max(
          10000,
          t("general_form_errors.max", {
            label: "Max registrations",
            maxValue: "10000",
          }),
        ),
      rsvp_button_text: Yup.string()
        .nullable()
        .max(25, t("general_form_errors.maximum_chars"))
        .when("n_type", {
          is: "EVENT",
          then: Yup.string().required(
            t("updates_create.about_rsvp_button_hint"),
          ),
          otherwise: Yup.string().nullable(),
        }),
      description: Yup.string().required(t("updates_create.about_desc_error")),
      target: Yup.string().required(t("updates_create.target_error")),
      html_content: Yup.string(),
      floors: Yup.array().when("target", {
        is: "floor",
        then: Yup.array().required(t("updates_create.target_floors_error")),
        otherwise: Yup.array(),
      }),
      companies: Yup.array().when("target", {
        is: "company",
        then: Yup.array().required(t("updates_create.target_companies_error")),
        otherwise: Yup.array(),
      }),
      buildings: Yup.array().when("target", {
        is: "building",
        then: Yup.array().required(t("updates_create.target_buildings_error")),
        otherwise: Yup.array(),
      }),
      n_type: Yup.string().required(t("updates_create.target_n_type_error")),
      image: Yup.mixed()
        .nullable()
        .notRequired()
        .test("file", t("updates_create.image_size_error"), (value) => {
          if (!value || !value.size) return true; // Skip validation if no file is uploaded
          return value.size <= MAX_FILE_SIZE; // Validate file size
        })
        .test("file", t("updates_create.image_type_error"), (value) => {
          if (!value || !value.name) return true; // Skip validation if no file is uploaded
          return ValidImageFileTypes.includes(
            value.name.split(".").pop().toLowerCase(),
          );
        }),
      available_at: Yup.date().required(
        t("updates_create.time_available_at_error"),
      ),
      available_until: Yup.date()
        .required(t("updates_create.time_available_until_error"))
        .test(
          "available_until_after_at",
          t("updates_create.time_available_order_error"),
          function (value) {
            const { available_at } = this.parent || {};
            if (!value || !available_at) return true;
            return new Date(value) >= new Date(available_at);
          },
        ),
    });
  }

  render() {
    const {
      classes,
      handleSubmit,
      initialValues,
      isEdit,
      isPublished,
      floors,
      companies,
      buildings,
      timezone,
      isCreate,
      isDuplicate,
      deleteNotification,
      duplicateNotification,
      lang,
    } = this.props;

    return (
      <Translation>
        {(t) => (
          <Formik
            enableReinitialize
            onSubmit={(values, form) => {
              handleSubmit(values, form, () => {
                form.setFieldValue("html_content", "");
              });
            }}
            initialValues={initialValues}
            validationSchema={this.generateValidationSchema(t)}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              handleChange,
              status,
              setValues,
              setFieldValue,
            }) => (
              <Form>
                <Grid container direction="row" spacing={2.5}>
                  {/* published text */}
                  <Grid size={{ xs: 12 }}>
                    <Grid container direction="row" justifyContent="start">
                      {/* <Grid size={{ xs: 6 }}>
                    <Typography variant="h5">
                      {isEdit ? "Edit Update" : "Create Update"}
                    </Typography>
                  </Grid> */}
                      <Grid size={{ xs: 6 }} className={classes.alignRight}>
                        {isPublished ? (
                          <Typography
                            variant="body1"
                            className={classes.publishedStatus}
                          >
                            ● {t("updates_edit.status_published")}
                          </Typography>
                        ) : null}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Grid container direction="row" spacing={2}>
                      {/* first section header and content */}
                      <Grid size={{ xs: 12 }}>
                        <EnhancedCard title={t("updates_create.about_title")}>
                          <Grid
                            container
                            direction="column"
                            spacing={2.5}
                            alignItems="stretch"
                          >
                            {/* 1. Type Field */}
                            <Grid size={{ xs: 12 }}>
                              <FormControl
                                className={classes.formControl}
                                disabled={isPublished}
                                error={touched.n_type && has(errors, "n_type")}
                              >
                                <FormHelperText className={classes.headerText}>
                                  {t("updates_create.about_type_label")}
                                </FormHelperText>
                                <Select
                                  name="n_type"
                                  id="n_type"
                                  defaultValue={initialValues.n_type}
                                  value={values.n_type || initialValues.n_type}
                                  onChange={(event) => {
                                    this.setState({
                                      showFloorsField:
                                        event.target.value === "floor",
                                    });
                                    setFieldValue("n_type", event.target.value);
                                  }}
                                >
                                  <MenuItem
                                    value="NOTIFICATION"
                                    key="NOTIFICATION"
                                  >
                                    {t(
                                      "updates_create.about_type_option_notification",
                                    )}
                                  </MenuItem>
                                  <MenuItem value="ALERT" key="ALERT">
                                    {t(
                                      "updates_create.about_type_option_alert",
                                    )}
                                  </MenuItem>
                                  <MenuItem value="SURVEY" key="SURVEY">
                                    {t(
                                      "updates_create.about_type_option_survey",
                                    )}
                                  </MenuItem>
                                  {!initialValues.ishilo && (
                                    <MenuItem value="EVENT" key="EVENT">
                                      {t(
                                        "updates_create.about_type_option_event",
                                      )}
                                    </MenuItem>
                                  )}
                                </Select>
                                {touched.n_type && has(errors, "n_type") ? (
                                  <FormHelperText>
                                    {get(errors, "n_type")}
                                  </FormHelperText>
                                ) : null}
                              </FormControl>
                            </Grid>

                            {/* 2. Title Field */}
                            <Grid size={{ xs: 12 }}>
                              <FormControl className={classes.formControl}>
                                <TextField
                                  error={touched.title && has(errors, "title")}
                                  id="title"
                                  type="name"
                                  name="title"
                                  label={t("updates_create.about_title_label")}
                                  value={values.title}
                                  onChange={handleChange}
                                  helperText={
                                    touched.title && get(errors, "title")
                                  }
                                  slotProps={{ htmlInput: { maxLength: 50 } }}
                                />
                                <FormHelperText>
                                  {t("updates_create.about_title_hint")}
                                </FormHelperText>
                              </FormControl>

                              {values.n_type === "EVENT" && (
                                <Grid size={{ xs: 12 }}>
                                  <FormControl
                                    className={classes.maxLimitControl}
                                  >
                                    <TextField
                                      error={
                                        touched.max_registrations &&
                                        has(errors, "max_registrations")
                                      }
                                      id="max_registrations"
                                      type="number"
                                      name="max_registrations"
                                      label={t(
                                        "updates_edit.max_registrations_placeholder",
                                      )}
                                      value={values.max_registrations ?? ""}
                                      onChange={handleChange}
                                      slotProps={{
                                        htmlInput: {
                                          min: 1,
                                          max: 10000,
                                          step: 1,
                                        },
                                      }}
                                      helperText={
                                        touched.max_registrations &&
                                        get(errors, "max_registrations")
                                      }
                                      disabled={isPublished}
                                    />
                                    <FormHelperText
                                      className={classes.captionText}
                                    >
                                      <span>
                                        {t(
                                          "updates_edit.max_registrations_hint",
                                        )}
                                      </span>
                                      <span>
                                        <span
                                          style={{
                                            textDecoration: "underline",
                                          }}
                                        >
                                          {t(
                                            "updates_edit.max_registrations_note_title",
                                          )}
                                        </span>
                                      </span>
                                      <span>
                                        {t(
                                          "updates_edit.max_registrations_note_text",
                                        )}
                                      </span>
                                    </FormHelperText>
                                  </FormControl>
                                </Grid>
                              )}

                              {/* 3. Body Field */}
                              <Grid
                                size={{ xs: 12 }}
                                className={classes.editorGroup}
                              >
                                <EnhancedInput
                                  id="html_content"
                                  type="text"
                                  name="html_content"
                                  label={
                                    <FormHelperText
                                      className={classes.bodyLabel}
                                    >
                                      <span className={classes.headerText}>
                                        {t("updates_create.about_body_title")}
                                      </span>
                                      <span className={classes.captionText}>
                                        {t("updates_create.about_body_hint")}
                                      </span>
                                    </FormHelperText>
                                  }
                                  fullWidth
                                  multiline
                                  disabled
                                  rows="12"
                                  error={
                                    touched.description &&
                                    has(errors, "html_content")
                                  }
                                  onChange={handleChange}
                                  value={values.html_content}
                                  style={{ display: "none" }}
                                />
                                <ReactTextEditor
                                  defaultValue={initialValues.html_content}
                                  value={values.html_content}
                                  handleChange={(html_content) => {
                                    setFieldValue("html_content", html_content);
                                  }}
                                  style={{
                                    height: "400px",
                                  }}
                                />
                              </Grid>

                              {!initialValues.ishilo && (
                                <Grid size={{ xs: 12 }}>
                                  {/* 4. RSVP Button Field */}
                                  <FormControl
                                    className={classes.rsvpFormControl}
                                  >
                                    <TextField
                                      error={
                                        touched.rsvp_button_text &&
                                        has(errors, "rsvp_button_text")
                                      }
                                      id="rsvpformbtn"
                                      type="text"
                                      name="rsvp_button_text"
                                      label={t(
                                        "updates_create.about_rsvp_button_label",
                                      )}
                                      value={values.rsvp_button_text}
                                      onChange={handleChange}
                                      helperText={
                                        touched.rsvp_button_text &&
                                        get(errors, "rsvp_button_text")
                                      }
                                      slotProps={{
                                        htmlInput: { maxLength: 25 },
                                      }}
                                    />
                                    <FormHelperText
                                      className={classes.captionText}
                                    >
                                      {t(
                                        "updates_create.about_rsvp_button_hint",
                                      )}
                                    </FormHelperText>
                                  </FormControl>
                                  {/* 5. URL Button Field */}
                                  {values.n_type !== "EVENT" && (
                                    <FormControl
                                      className={classes.urlFormControl}
                                    >
                                      <TextField
                                        error={
                                          touched.rsvp_form_url &&
                                          has(errors, "rsvp_form_url")
                                        }
                                        id="rsvpform"
                                        type="url"
                                        name="rsvp_form_url"
                                        label={t(
                                          "updates_create.about_rsvp_label",
                                        )}
                                        value={values.rsvp_form_url}
                                        onChange={handleChange}
                                        helperText={
                                          touched.rsvp_form_url &&
                                          get(errors, "rsvp_form_url")
                                        }
                                        slotProps={{
                                          htmlInput: { maxLength: 255 },
                                        }}
                                      />
                                      <FormHelperText
                                        className={classes.captionText}
                                      >
                                        {t("updates_create.about_rsvp_hint")}
                                      </FormHelperText>
                                    </FormControl>
                                  )}
                                </Grid>
                              )}

                              {/* 6. Push Notification Field */}
                              <Grid
                                className={classes.formGroup}
                                size={{ xs: 12 }}
                              >
                                <FormControl className={classes.formControl}>
                                  <TextField
                                    error={
                                      touched.description &&
                                      has(errors, "description")
                                    }
                                    id="description"
                                    type="text"
                                    name="description"
                                    label={t(
                                      "updates_create.about_push_notification_title",
                                    )}
                                    value={values.description}
                                    onChange={handleChange}
                                    helperText={
                                      touched.description &&
                                      get(errors, "description")
                                    }
                                    slotProps={{
                                      htmlInput: { maxLength: 155 },
                                    }}
                                  />
                                  <FormHelperText
                                    className={classes.captionText}
                                  >
                                    {t(
                                      "updates_create.about_push_notification_hint",
                                    )}
                                  </FormHelperText>
                                </FormControl>
                              </Grid>

                              {/* 7. Image Upload Field */}
                              <Grid size={{ xs: 12 }} container rowSpacing={3}>
                                <ImageUploader
                                  buttonId="notification_image_uploader_btn"
                                  inputId="notification_image_input"
                                  className={classes.formControl}
                                  singleRowLayout
                                  inlineGap={12}
                                  imageContainer={{
                                    width: "37%",
                                    margin: 0,
                                  }}
                                  buttonContainer={{
                                    paddingLeft: 0,
                                  }}
                                  imageStyle={{
                                    width: "100%",
                                    height: "100%",
                                  }}
                                  label={
                                    <FormHelperText
                                      className={classes.headerText}
                                    >
                                      <span>
                                        {t("updates_create.about_image_title")}
                                      </span>
                                    </FormHelperText>
                                  }
                                  captionStyle={{
                                    whiteSpace: "pre-wrap",
                                    color: "rgba(0, 0, 0, 0.54)",
                                  }}
                                  caption={
                                    <FormHelperText
                                      className={classes.captionText}
                                    >
                                      <span>
                                        {t("updates_create.about_image_hint")}
                                      </span>
                                      <span className={classes.captionText}>
                                        <span
                                          style={{
                                            textDecoration: "underline",
                                          }}
                                        >
                                          {t(
                                            "updates_create.about_image_note_title",
                                          )}
                                        </span>
                                        {t(
                                          "updates_create.about_image_note_text",
                                        )}
                                      </span>
                                    </FormHelperText>
                                  }
                                  initialImageUrl={
                                    typeof values.image === "string"
                                      ? values.image
                                      : null
                                  }
                                  touched={!!touched.image}
                                  onChange={(file) => {
                                    setFieldValue("image", file);
                                  }}
                                />
                                {touched.image && errors.image && (
                                  <FormHelperText error>
                                    {errors.image}
                                  </FormHelperText>
                                )}
                                <Grid
                                  size={{ xs: 12 }}
                                  style={{ display: "none" }}
                                >
                                  <EnhancedInput
                                    id="image"
                                    type="text"
                                    name="image"
                                    label="Image URL"
                                    fullWidth
                                    error={
                                      touched.description &&
                                      has(errors, "image")
                                    }
                                    onChange={handleChange}
                                    value={
                                      typeof values.image === "string"
                                        ? values.image
                                        : ""
                                    }
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </EnhancedCard>
                      </Grid>

                      {/* push notification section */}
                      <Grid size={{ xs: 12 }}>
                        <EnhancedCard
                          title={t("updates_create.push_notification_title")}
                        >
                          <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                              id="send_push_notification"
                              name="send_push_notification"
                              className={isPublished ? classes.disabled : null}
                              disabled={isPublished}
                              control={
                                <Checkbox
                                  checked={values.send_push_notification}
                                  onChange={(event, checked) => {
                                    setFieldValue(
                                      "send_push_notification",
                                      checked,
                                    );
                                  }}
                                />
                              }
                              label={t(
                                "updates_create.push_notification_checkbox",
                              )}
                            ></FormControlLabel>
                          </Grid>
                        </EnhancedCard>
                      </Grid>
                      {/* where & when update section */}
                      <Grid size={{ xs: 12 }}>
                        <EnhancedCard
                          title={t("updates_create.time_target_title")}
                        >
                          <Grid size={{ xs: 12 }}>
                            <FormControl
                              className={classes.formControl}
                              disabled={isPublished}
                              error={touched.target && has(errors, "target")}
                            >
                              <FormHelperText className={classes.headerText}>
                                {t("updates_create.target_label")}
                              </FormHelperText>
                              <Select
                                name="target"
                                id="target"
                                value={values.target}
                                onChange={(event) => {
                                  this.setState({
                                    showBuildingsField:
                                      event.target.value === "building",
                                    showFloorsField:
                                      event.target.value === "floor",
                                    showCompanyField:
                                      event.target.value === "company",
                                  });
                                  setFieldValue("target", event.target.value);
                                }}
                              >
                                <MenuItem value="building" key="building">
                                  {t("updates_create.target_option_building")}
                                </MenuItem>
                                <MenuItem value="floor" key="floor">
                                  {t("updates_create.target_option_floor")}
                                </MenuItem>
                                {!initialValues.ishilo && (
                                  <MenuItem value="company" key="company">
                                    {t("updates_create.target_option_company")}
                                  </MenuItem>
                                )}
                              </Select>
                              {touched.target && has(errors, "target") ? (
                                <FormHelperText>
                                  {get(errors, "target")}
                                </FormHelperText>
                              ) : null}
                            </FormControl>
                          </Grid>
                          {this.state.showFloorsField && (
                            <Grid size={{ xs: 4 }}>
                              <FormControl
                                disabled={isPublished}
                                className={classes.formControl}
                              >
                                <FormHelperText style={{ fontSize: "0.9rem" }}>
                                  {t("updates_create.target_floor_label")}
                                </FormHelperText>
                                <Select
                                  name="floors"
                                  id="floors"
                                  multiple
                                  value={values.floors}
                                  MenuProps={{ className: classes.menu }}
                                  onChange={(e) => {
                                    setFieldValue("floors", e.target.value);
                                  }}
                                  renderValue={(selected) => {
                                    return (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        {selected.map((value) => (
                                          <Chip
                                            key={value}
                                            label={this.displayFloorLabelById(
                                              value,
                                              floors,
                                            )}
                                          />
                                        ))}
                                      </div>
                                    );
                                  }}
                                >
                                  {floors.map((floor) => (
                                    <MenuItem key={floor.id} value={floor.id}>
                                      {floor.level}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {touched.target && has(errors, "floors") ? (
                                  <FormHelperText>
                                    {get(errors, "floors")}
                                  </FormHelperText>
                                ) : null}
                              </FormControl>
                            </Grid>
                          )}

                          {!isEdit &&
                            !initialValues.ishilo &&
                            this.state.showBuildingsField && (
                              <Grid size={{ xs: 4 }}>
                                <FormControl
                                  disabled={isPublished}
                                  className={classes.formControl}
                                >
                                  <FormHelperText
                                    style={{ fontSize: "0.9rem" }}
                                  >
                                    {t("updates_create.target_building_label")}
                                  </FormHelperText>
                                  <Select
                                    name="buildings"
                                    id="buildings"
                                    multiple
                                    value={values.buildings}
                                    MenuProps={{ className: classes.menu }}
                                    onChange={(e) => {
                                      const selectedValues = e.target.value;
                                      const allBuildingIds = buildings.map(
                                        (building) => building.building_id,
                                      );

                                      if (
                                        (isDuplicate || isCreate) &&
                                        selectedValues.includes("ALL_BUILDINGS")
                                      ) {
                                        setFieldValue(
                                          "buildings",
                                          allBuildingIds,
                                        );
                                        return;
                                      }

                                      setFieldValue(
                                        "buildings",
                                        selectedValues.filter(
                                          (value) => value !== "ALL_BUILDINGS",
                                        ),
                                      );
                                    }}
                                    renderValue={(selected) => {
                                      const allBuildingIds = buildings.map(
                                        (building) => building.building_id,
                                      );
                                      const allBuildingsSelected =
                                        (isDuplicate || isCreate) &&
                                        allBuildingIds.length > 0 &&
                                        allBuildingIds.every((buildingId) =>
                                          selected.includes(buildingId),
                                        );

                                      if (allBuildingsSelected) {
                                        return (
                                          <Chip
                                            label={t(
                                              "updates_create.target_allbuilding_label",
                                            )}
                                          />
                                        );
                                      }

                                      return (
                                        <div
                                          style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          {selected.map((value) => (
                                            <Chip
                                              key={value}
                                              label={this.displayBuildingNameById(
                                                value,
                                                buildings,
                                              )}
                                            />
                                          ))}
                                        </div>
                                      );
                                    }}
                                  >
                                    {isDuplicate || isCreate ? (
                                      <MenuItem value="ALL_BUILDINGS">
                                        {t(
                                          "updates_create.target_allbuilding_label",
                                        )}
                                      </MenuItem>
                                    ) : null}
                                    {buildings.map((building) => (
                                      <MenuItem
                                        key={building.building_id}
                                        value={building.building_id}
                                      >
                                        {building.building_name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {touched.target &&
                                  has(errors, "buildings") ? (
                                    <FormHelperText>
                                      {get(errors, "buildings")}
                                    </FormHelperText>
                                  ) : null}
                                </FormControl>
                              </Grid>
                            )}

                          {!initialValues.ishilo &&
                            this.state.showCompanyField && (
                              <Grid size={{ xs: 4 }}>
                                <FormControl
                                  disabled={isPublished}
                                  className={classes.formControl}
                                >
                                  <FormHelperText
                                    style={{ fontSize: "0.9rem" }}
                                  >
                                    {t("updates_create.target_company_label")}
                                  </FormHelperText>
                                  <Select
                                    name="companies"
                                    id="companies"
                                    value={values.companies}
                                    MenuProps={{ className: classes.menu }}
                                    onChange={(e) => {
                                      setFieldValue("companies", [
                                        e.target.value,
                                      ]);
                                    }}
                                    renderValue={(selected) => {
                                      return (
                                        <div
                                          style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          <Chip
                                            key={selected}
                                            label={this.displayCompanyNameById(
                                              selected,
                                              companies,
                                            )}
                                          />
                                        </div>
                                      );
                                    }}
                                  >
                                    {companies.map((company) => (
                                      <MenuItem
                                        key={company.id}
                                        value={company.id}
                                      >
                                        {company.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {touched.target &&
                                  has(errors, "companies") ? (
                                    <FormHelperText>
                                      {get(errors, "companies")}
                                    </FormHelperText>
                                  ) : null}
                                </FormControl>
                              </Grid>
                            )}
                          <Grid
                            size={{ xs: 12, sm: 6 }}
                            className={classes.dateField}
                          >
                            <CompositeDateTimePickers
                              labelId="start-datetime-label"
                              dateInputId="start-date-input"
                              timeInputId="start-time-input"
                              label={t("updates_create.time_available_on")}
                              name="available_at"
                              disabled={isPublished}
                              value={values.available_at}
                              timezone={timezone}
                              error={has(errors, "available_at")}
                              handleChange={(value) => {
                                setFieldValue("available_at", value);
                              }}
                            />
                          </Grid>
                          <Grid
                            size={{ xs: 12, sm: 6 }}
                            className={classes.dateField}
                          >
                            <CompositeDateTimePickers
                              labelId="end-datetime-label"
                              dateInputId="end-date-input"
                              timeInputId="end-time-input"
                              label={t("updates_create.time_available_until")}
                              name="available_until"
                              value={values.available_until}
                              timezone={timezone}
                              error={
                                touched.available_until &&
                                has(errors, "available_until")
                              }
                              helperText={
                                get(touched, "available_until") &&
                                get(errors, "available_until")
                              }
                              handleChange={(value) => {
                                setFieldValue("available_until", value);
                              }}
                            />
                          </Grid>
                        </EnhancedCard>
                      </Grid>

                      {/* event contest section */}
                      {values.n_type === "EVENT" && (
                        <Grid size={{ xs: 12 }}>
                          <EnhancedCard
                            title={t("updates_create.event_contest_title")}
                          >
                            <Grid size={{ xs: 12 }}>
                              <FormControlLabel
                                id="isContest"
                                name="isContest"
                                className={
                                  isPublished ? classes.disabled : null
                                }
                                disabled={isPublished}
                                control={
                                  <Checkbox
                                    checked={values.isContest}
                                    onChange={(event) => {
                                      setFieldValue(
                                        "isContest",
                                        event.target.checked,
                                      );
                                    }}
                                  />
                                }
                                label={t(
                                  "updates_create.event_contest_checkbox",
                                )}
                              />
                            </Grid>
                          </EnhancedCard>
                        </Grid>
                      )}
                      {/* end event contest section */}
                      {/* edit update section */}
                      <Grid size={{ xs: 12 }} className={classes.button}>
                        <Grid
                          container
                          direction="row"
                          alignItems="center"
                          spacing={2.5}
                        >
                          <Grid size={{ xs: 6 }}>
                            <SubmitButton isSubmitting={isSubmitting}>
                              {isEdit
                                ? t("updates_edit.edit_btn")
                                : t("updates_create.submit")}
                            </SubmitButton>
                          </Grid>
                          <Grid size={{ xs: 6 }} className={classes.alignRight}>
                            {isEdit && !isPublished ? (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                  deleteNotification();
                                }}
                              >
                                {t("updates_edit.delete_btn")}
                              </Button>
                            ) : null}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Middle section */}

                  <Grid size={{ xs: 4 }}>
                    <Grid container direction="row">
                      <Grid size={{ xs: 12 }}>
                        <Grid container direction="column">
                          <Grid>
                            <UpdatePreview
                              customValues={values.custom_values}
                              shortDescription={values.description}
                              html_content={values.html_content}
                              title={values.title}
                              date={formatDate(
                                values.available_at,
                                lang === '"fr"'
                                  ? "DD MMM, YYYY"
                                  : "MMM DD, YYYY",
                                lang === '"fr"' ? "fr" : "en",
                              )} // : moment(values.available_at).format("MMM DD, YYYY")}
                              imagePreviewUrl={values.image}
                              buttontext={values.rsvp_button_text}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Duplicate Button section */}

                  <Grid size={{ xs: 2 }} className={classes.duplicateCol}>
                    <Grid container direction="column">
                      <Grid size={{ xs: 0 }}>
                        {isEdit ? (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              duplicateNotification();
                            }}
                          >
                            {t("updates_list.th_duplicate")}
                          </Button>
                        ) : null}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                {get(status, "success") ? (
                  <SimpleSnackbar
                    message={
                      isEdit
                        ? t("updates_edit.success_message")
                        : t("updates_create.success_message")
                    }
                  />
                ) : null}
                {get(status, "showSnackbar") ? (
                  <SimpleSnackbar
                    message={t("updates_create.success_message")}
                    autoHideDuration={2000}
                    onClose={() => {
                      this.props.redirectToCreate();
                    }}
                  />
                ) : null}
                {has(errors, "http") ? (
                  <SimpleSnackbar
                    message={t("general_form_errors.error_response")}
                  />
                ) : null}
              </Form>
            )}
          </Formik>
        )}
      </Translation>
    );
  }
}

const styles = (theme) => ({
  publishedStatus: {
    marginBottom: "20px",
  },
  headerText: {
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: "16px",
    fontWeight: "700",
  },
  dateField: {
    minWidth: 0,
    marginTop: "15px",
  },
  captionText: {
    fontSize: "0.75rem",
    lineHeight: "16px",
  },
  bodyLabel: {
    marginLeft: 0,
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  editorGroup: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "70px",
  },
  button: {
    marginTop: theme.spacing(1.5),
  },
  maxLimitControl: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
    width: "100%",
  },
  formControl: {
    marginBottom: theme.spacing(1),
    width: "100%",
  },
  rsvpFormControl: {
    marginBottom: theme.spacing(3),
    width: "100%",
  },
  urlFormControl: {
    marginBottom: theme.spacing(3),
    width: "100%",
  },
  disabled: {
    opacity: 0.6,
  },
  alignRight: {
    textAlign: "right",
  },
  duplicateCol: {
    display: "flex",
    justifyContent: "flex-end",
    paddingRight: "20px",
  },
  menu: {
    width: "100%",
  },
});

NotificationForm.propTypes = {
  classes: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  floors: PropTypes.array,
  buildings: PropTypes.array,
  companies: PropTypes.array,
  timezone: PropTypes.string.isRequired,
  isCreate: PropTypes.bool,
  isEdit: PropTypes.bool,
  isDuplicate: PropTypes.bool,
  isContest: PropTypes.bool,
  eventId: PropTypes.string,
  deleteNotification: PropTypes.func,
};

export default withStyles(styles)(NotificationForm);
